import {
  GridColDef,
  GridColumnGroupingModel,
  GridRenderCellParams,
} from "@mui/x-data-grid";
import { AppDataGrid } from "../../../components/datagrid/AppDataGrid";
import { Controller, useFormContext } from "react-hook-form";
import { TextField, Typography } from "@mui/material";
import alertService from "../../../components/alert/alertService";
import { ApiError } from "../../../api/ApiError";
import { goalSettingService } from "../../../service/goal-setting.service";
import { tableStyle } from "./AddGoalTableStyle";
import { useTranslation } from "react-i18next";
import { common, input, reduce } from "../../../i18n";
import { GoalPlanBaseline } from "../../../models/GoalSetting";
import NumberFormatterUtils from "../../../utils/numberFormatterUtils";
import { NumericFormat } from "react-number-format";

interface BaselineFormValues {
  baseYear: number | null;
  startYear: number | null;
  targetYear: number | null;
  bauEmission: number;
  goalNetEmission: number;
  percentReduction: number;
  targetAbsoluteRate: boolean;
  percentAnnualGrowthRate: number;
  startAbsoluteRate: boolean;
  rows: GoalPlanBaseline[];
}

export const BaselineSettingTable = () => {
  const { t } = useTranslation([common, input, reduce], { nsMode: "fallback" });
  // const { control, watch, setValue, getValues } = useFormContext();

  const { watch, setValue, getValues, reset, control, formState } =
    useFormContext<BaselineFormValues>();
  const rows = watch("rows") || [];

  const handleUpdate = async (
    year: number,
    val: number,
    field: "percentReduction" | "percentAnnualGrowthRate",
  ) => {
    const current = getValues();
    const oldRows: GoalPlanBaseline[] = current.rows || [];

    // ✅ อัปเดตค่าในแถวที่แก้ไข (เฉพาะช่องที่ blur)
    const updatedRows = oldRows.map((r) =>
      r.year === year ? { ...r, [field]: val } : r,
    );

    // ✅ อัปเดตลงใน react-hook-form ทันที
    setValue("rows", updatedRows, { shouldDirty: true });

    // ✅ ตัดค่าไม่จำเป็นออกก่อนยิง API
    const { goalNetEmission, rows, ...rest } = current;
    const payLoad: any = { ...rest };

    // ✅ ถ้า absoluteRate ทั้งคู่เป็น true → ไม่ต้องส่ง plans
    if (
      !(
        current.targetAbsoluteRate === true &&
        current.startAbsoluteRate === true
      )
    ) {
      // ✅ ส่ง plans จาก updatedRows ที่เพิ่งอัปเดต
      payLoad.plans = updatedRows
        .map((r) => ({
          year: r.year,
          percentReduction: r.percentReduction,
          percentAnnualGrowthRate: r.percentAnnualGrowthRate,
        }))
        .filter(
          (p) =>
            p.percentReduction != null && p.percentAnnualGrowthRate != null,
        );
    }

    // ✅ ตัด rows ออกทุกกรณี
    delete payLoad.rows;

    // console.log("payload", payLoad);

    try {
      // alertService.showLoading();
      const result = await goalSettingService.generatePlanBaseline(payLoad);
      // alertService.closeLoading();

      // ✅ อัปเดตตารางจากผลลัพธ์ใหม่ของ API
      setValue("rows", result?.plans || [], { shouldDirty: false });
      alertService.mixin("Updated-Success");
    } catch (err) {
      console.error("generatePlanBaseline error:", err);
      if (!(err instanceof ApiError)) {
        await alertService.error("Error updating baseline plan");
      }
    }
  };

  const columns: GridColDef[] = [
    {
      field: "be",
      headerName: t("BE"),
      width: 100,
      align: "center",
      headerAlign: "center",
      renderCell: (params: GridRenderCellParams<GoalPlanBaseline>) => {
        const beYear = params.row.year ? params.row.year + 543 : "";
        return (
          <Typography
            variant="body1"
            sx={{
              width: "100%",
              textAlign: "center",
            }}
          >
            {beYear}
          </Typography>
        );
      },
    },
    {
      field: "ad",
      headerName: t("AD"),
      width: 100,
      align: "center",
      headerAlign: "center",
      renderCell: (params: GridRenderCellParams<GoalPlanBaseline>) => {
        const adYear = params.row.year;
        return (
          <Typography
            variant="body1"
            sx={{
              width: "100%",
              textAlign: "center",
            }}
          >
            {adYear}
          </Typography>
        );
      },
    },

    {
      field: "percentAnnualGrowthRate",
      headerName: `% ${t("Annual-Growth-Rate")}`,
      flex: 1,
      align: "right",
      headerAlign: "center",
      renderCell: (params) => {
        if (params.value === null || params.value === undefined) return null;

        return (
          <NumericFormat
            value={params.row.percentAnnualGrowthRate ?? ""}
            onBlur={(event) => {
              const n = parseFloat(String(event.target.value ?? ""));
              handleUpdate(
                params.row.year,
                Number.isFinite(n) ? n : 0,
                "percentAnnualGrowthRate",
              );
            }}
            thousandSeparator=","
            allowLeadingZeros
            allowNegative={false}
            decimalScale={2}
            fixedDecimalScale
            isAllowed={(vals) =>
              vals.floatValue == null ||
              (vals.floatValue >= 0 && vals.floatValue <= 100)
            }
            customInput={TextField}
            onFocus={(e) => e.target.select()}
            inputProps={{ style: { textAlign: "right" } }}
            disabled={watch("startAbsoluteRate")}
            size="small"
            fullWidth
            onKeyDown={(event) => event.stopPropagation()}
            onKeyUp={async (event: React.KeyboardEvent) => {
              switch (event.key) {
                case "Enter":
                  (document.activeElement as HTMLElement).blur();
                  break;
                default:
                  break;
              }
            }}
          />
        );
      },
    },

    {
      field: "bauEmission",
      // headerName: `${t("BAU-Emissions")} (tCO₂e)`,
      renderHeader: () => (
        <span>
          {t("BAU-Emissions")} (tCO<sub>2</sub>e)
        </span>
      ),
      flex: 1,
      align: "right",
      headerAlign: "center",
      valueFormatter: (value) =>
        value === null ? "" : NumberFormatterUtils.numberFormat(value),
    },
    {
      field: "percentReduction",
      headerName: `% ${t("Reduction")}`,
      headerAlign: "center",
      width: 150,
      renderCell: (params) => {
        if (params.value === null || params.value === undefined) return null;

        return (
          <NumericFormat
            value={params.value ?? ""}
            onBlur={(event) => {
              const n = parseFloat(String(event.target.value ?? ""));
              handleUpdate(
                params.row.year,
                Number.isFinite(n) ? n : 0,
                "percentReduction",
              );
            }}
            thousandSeparator=","
            allowLeadingZeros
            allowNegative={false}
            decimalScale={2}
            fixedDecimalScale
            isAllowed={(vals) =>
              vals.floatValue == null ||
              (vals.floatValue >= 0 && vals.floatValue <= 100)
            }
            customInput={TextField}
            onFocus={(e) => e.target.select()}
            inputProps={{ style: { textAlign: "right" } }}
            disabled={watch("targetAbsoluteRate")}
            size="small"
            fullWidth
            onKeyDown={(event) => event.stopPropagation()}
            onKeyUp={async (event: React.KeyboardEvent) => {
              switch (event.key) {
                case "Enter":
                  (document.activeElement as HTMLElement).blur();
                  break;
                default:
                  break;
              }
            }}
          />
        );
      },
    },

    {
      field: "targetEmission",
      // headerName: `${t("Emission-Reduction")} (tCO₂e)`,
      renderHeader: () => (
        <span>
          {t("Emission-Reduction")} (tCO<sub>2</sub>e)
        </span>
      ),
      flex: 1,
      align: "right",
      headerAlign: "center",
      valueFormatter: (value) =>
        value === null ? "" : NumberFormatterUtils.numberFormat(value),
    },
    {
      field: "targetReduction",
      // headerName: `${t("Target-Reduction")} (tCO₂e)`,
      renderHeader: () => (
        <span>
          {t("Target-Reduction")} (tCO<sub>2</sub>e)
        </span>
      ),
      flex: 1,
      align: "right",
      headerAlign: "center",
      valueFormatter: (value) =>
        value === null ? "" : NumberFormatterUtils.numberFormat(value),
    },
  ];

  const columnGroup: GridColumnGroupingModel = [
    {
      groupId: t("Year"),
      children: [{ field: "be" }, { field: "ad" }],
      headerAlign: "center",
    },
  ];

  return (
    <AppDataGrid
      columns={columns}
      rows={rows}
      columnGroupingModel={columnGroup}
      getRowId={(r) => r.year}
      sx={tableStyle()}
      autoHeight
      hideFooter
    />
  );
};
